import { useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  businessToday, defaultPeriod, monthEnd, monthsBetween, normalizePeriod, parseMonths, presetRange, toApiParams, PERIOD_MODES,
} from '../lib/period';

const STORAGE_KEY = 'accounts.period';
const URL_KEYS = ['from', 'to', 'preset', 'all', 'month', 'months'];

const readStored = () => {
  try { return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null'); } catch { return null; }
};

const fromSearch = (sp) => ({
  from: sp.get('from'), to: sp.get('to'), preset: sp.get('preset'),
  allTime: sp.get('all') === '1', months: sp.get('months') || sp.get('month'),
});

const hasPeriod = (raw) => raw.allTime || raw.from || raw.to || raw.months;

/**
 * The ONE shared Accounts period. URL (?from&to[&preset] | ?all=1, plus ?months for Tax) is canonical;
 * sessionStorage only carries it across navigations whose links have no query string.
 */
export const usePeriod = (mode = PERIOD_MODES.RANGE) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlRaw = fromSearch(searchParams);
  const raw = hasPeriod(urlRaw) ? urlRaw : (readStored() || {});

  // Explicit tax month selection (?months=a,b or legacy ?month=a) — kept even when non-contiguous.
  const months = parseMonths(raw.months);
  const shared = normalizePeriod(raw)
    || (months ? { from: `${months[0]}-01`, to: monthEnd(months[months.length - 1]), preset: null, allTime: false } : null)
    || defaultPeriod();
  const monthsKey = months ? months.join(',') : null;

  // Write the effective shared period into the URL (refresh/bookmark) and session (navigation).
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    URL_KEYS.forEach((k) => next.delete(k));
    if (shared.allTime) next.set('all', '1');
    else {
      next.set('from', shared.from);
      next.set('to', shared.to);
      if (shared.preset) next.set('preset', shared.preset);
    }
    if (monthsKey) next.set('months', monthsKey);
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ ...shared, months: monthsKey }));
    if (next.toString() !== searchParams.toString()) setSearchParams(next, { replace: true });
  }, [shared.from, shared.to, shared.preset, shared.allTime, monthsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const write = useCallback((p, nextMonths) => {
    const next = new URLSearchParams(searchParams);
    URL_KEYS.forEach((k) => next.delete(k));
    if (p.allTime) next.set('all', '1');
    else {
      next.set('from', p.from);
      next.set('to', p.to);
      if (p.preset) next.set('preset', p.preset);
    }
    if (nextMonths) next.set('months', nextMonths.join(','));
    setSearchParams(next);
  }, [searchParams, setSearchParams]);

  // Changing the range clears any explicit tax month selection so Tax follows the new range.
  const setRange = useCallback((from, to, preset = null) => write({ from, to, preset, allTime: false }, null), [write]);
  const setPreset = useCallback((preset) => {
    const r = presetRange(preset);
    if (r) write({ from: r[0], to: r[1], preset, allTime: false }, null);
  }, [write]);
  const setAllTime = useCallback(() => write({ allTime: true }, null), [write]);
  const reset = useCallback(() => write(defaultPeriod(), null), [write]);
  // Tax months → explicit list; rest of Accounts gets the broad range first-day → last-day.
  const setMonths = useCallback((list) => {
    const sorted = [...new Set(list)].sort();
    if (!sorted.length) return;
    write({ from: `${sorted[0]}-01`, to: monthEnd(sorted[sorted.length - 1]), preset: null, allTime: false }, sorted);
  }, [write]);

  // Pages without ALL_TIME use month-to-date while the shared state stays all-time.
  const allTimeUnsupported = shared.allTime && mode !== PERIOD_MODES.RANGE_ALL_TIME;
  const effective = allTimeUnsupported ? defaultPeriod() : shared;

  // Tax pages: explicit months > months touched by the shared range (pre-checked, flagged) > current month.
  const derivedMonths = shared.allTime
    ? [businessToday().slice(0, 7)]
    : monthsBetween(shared.from.slice(0, 7), shared.to.slice(0, 7));
  const activeMonths = months || derivedMonths;

  const apiParams = useMemo(() => toApiParams(effective), [effective.from, effective.to, effective.preset, effective.allTime]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    period: effective, shared, apiParams, allTimeUnsupported,
    months: activeMonths, monthsAreExplicit: !!months, setRange, setPreset, setAllTime, reset, setMonths,
  };
};

export default usePeriod;
